import React from "react";
import { Box, IconButton, InputAdornment, Stack, Typography } from "@mui/material";
import { Info } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { updateStateData } from "../../../../Store/actions";
import { checkUserDetailsFromCookie } from "../../../../api_calls/userManagement";
import { fetchCountryId } from "../../../../api_calls/infrastructure";
import TextInputField from "../../../../components/commonComponents/TextInputs/TextInputField";
import SingleSelect from "../../../../components/commonComponents/Selectors/SingleSelect";
import StoreOwnerField from "../StoreOwnerField";
import {
  HtmlTooltip,
  StyledDarkRedInformationTypography,
} from "../../../../components/commonComponents/StyledComponents/styled";
import ErrorMessageText from "../../../../components/commonComponents/Errors/ErrorMessageText";

const StoreGeneralInfo = ({ fieldErrors }) => {
  const userStateDetails = useSelector((state) => state.userSelections);
  const loggedUserDetails = checkUserDetailsFromCookie();
  const dispatchAction = useDispatch();

  const [newRegion, setNewRegion] = React.useState(false);

  const handleFieldChange = (storeLocData) => {
    const existing =
      typeof userStateDetails.new_store_loc_data !== "undefined" ? userStateDetails.new_store_loc_data : {};
    dispatchAction(updateStateData({ new_store_loc_data: { ...existing, ...storeLocData } }));
  };

  const fieldErr = (key) =>
    typeof fieldErrors !== "undefined" && typeof fieldErrors[key] !== "undefined" && fieldErrors[key] !== ""
      ? fieldErrors[key]
      : "";

  return (
    <>
      <Box height={20} />
      <Stack direction={"row"} spacing={4}>
        <TextInputField
          fieldRequired={true}
          elemId="store_name"
          label="Name"
          fieldName="store_name"
          showlabel={true}
          defaultVal={userStateDetails?.new_store_loc_data?.store_name ?? ""}
          inputType={"text"}
          onChangeCall={(e) => {
            e.preventDefault();
            handleFieldChange({ store_name: e.target.value });
          }}
          fieldError={fieldErr("store_name")}
        />
        {/* Country — read-only, driven by logged-in user workspace */}
        <SingleSelect
          enableFullWidth={true}
          label="Country*"
          elemId="storeCountry"
          readOnlyFlag={true}
          disableSearch={true}
          sortOptions={false}
          options={(userStateDetails.countries_list || []).map((country) => ({
            label: country.name,
            id: country.id,
          }))}
          defaultVal={
            loggedUserDetails.success === true &&
            loggedUserDetails.user_detail.current_workspace.country.name !== ""
              ? {
                  label: loggedUserDetails.user_detail.current_workspace.country.name,
                  id: fetchCountryId(loggedUserDetails.user_detail.current_workspace.country.name),
                }
              : ""
          }
          selectionCheck={(option, value) => option.id === value.id}
          onChangeCall={(selectedOption) => {
            handleFieldChange({
              store_country: selectedOption.label,
              store_country_id: selectedOption.id,
            });
          }}
          fieldError={fieldErr("store_country")}
        />
      </Stack>

      <Box height={47} />
      <Stack direction={"row"} spacing={4}>
        <SingleSelect
          enableFullWidth={true}
          label="Region*"
          elemId={"regionSelectionForStore"}
          options={typeof userStateDetails.regions_list !== "undefined" ? userStateDetails.regions_list : []}
          defaultVal={userStateDetails?.new_store_loc_data?.store_region_obj ?? {}}
          selectionCheck={(option, value) => option.id === value.id}
          onChangeCall={(selectedOption) => {
            handleFieldChange({
              store_region: selectedOption.label,
              store_region_id: selectedOption.id,
              store_region_obj: selectedOption,
              new_region: false,
            });
            setNewRegion(false);
          }}
          fieldError={fieldErr("store_region")}
        />
        <TextInputField
          fieldRequired={true}
          elemId="store_street"
          label="Street & No."
          fieldName="store_street"
          showlabel={true}
          defaultVal={userStateDetails?.new_store_loc_data?.store_street ?? ""}
          inputType={"text"}
          onChangeCall={(e) => {
            e.preventDefault();
            handleFieldChange({ store_street: e.target.value });
          }}
          fieldError={fieldErr("store_street")}
        />
      </Stack>

      <Box height={47} />
      <Stack direction={"row"} spacing={4}>
        <TextInputField
          fieldRequired={true}
          elemId="store_zip"
          label="Zip Code"
          fieldName="store_zip"
          showlabel={true}
          defaultVal={userStateDetails?.new_store_loc_data?.store_zip ?? ""}
          inputType={"text"}
          onChangeCall={(e) => {
            e.preventDefault();
            handleFieldChange({ store_zip: e.target.value });
          }}
          fieldError={fieldErr("store_zip")}
        />
        <TextInputField
          fieldRequired={true}
          elemId="store_city"
          label="City"
          fieldName="store_city"
          showlabel={true}
          defaultVal={userStateDetails?.new_store_loc_data?.store_city ?? ""}
          inputType={"text"}
          onChangeCall={(e) => {
            e.preventDefault();
            handleFieldChange({ store_city: e.target.value });
          }}
          fieldError={fieldErr("store_city")}
        />
      </Stack>

      <Box height={47} />
      <Stack direction={"row"} spacing={4}>
        <TextInputField
          fieldRequired={true}
          elemId="store_latitude"
          label="Latitude"
          fieldName="store_latitude"
          showlabel={true}
          defaultVal={userStateDetails?.new_store_loc_data?.latitude ?? ""}
          inputType={"text"}
          onChangeCall={(e) => {
            e.preventDefault();
            handleFieldChange({ latitude: e.target.value });
          }}
          fieldError={fieldErr("latitude")}
          iconProps={{
            endAdornment: (
              <InputAdornment position="end">
                <HtmlTooltip
                  title={
                    <Box width={250}>
                      <Typography variant="body2">
                        Please use accurate information for latitude and longitude. We recommend using the information
                        displayed by Google Maps. The order of values used by Google Maps is latitude followed by
                        longitude.
                      </Typography>
                    </Box>
                  }
                >
                  <IconButton>
                    <Info />
                  </IconButton>
                </HtmlTooltip>
              </InputAdornment>
            ),
          }}
        />
        <TextInputField
          fieldRequired={true}
          elemId="store_longitude"
          label="Longitude"
          fieldName="store_longitude"
          showlabel={true}
          defaultVal={userStateDetails?.new_store_loc_data?.longitude ?? ""}
          inputType={"text"}
          onChangeCall={(e) => {
            e.preventDefault();
            handleFieldChange({ longitude: e.target.value });
          }}
          fieldError={fieldErr("longitude")}
          iconProps={{
            endAdornment: (
              <InputAdornment position="end">
                <HtmlTooltip
                  title={
                    <Box width={250}>
                      <Typography variant="body2">
                        Please use accurate information for latitude and longitude. We recommend using the information
                        displayed by Google Maps. The order of values used by Google Maps is latitude followed by
                        longitude.
                      </Typography>
                    </Box>
                  }
                >
                  <IconButton>
                    <Info />
                  </IconButton>
                </HtmlTooltip>
              </InputAdornment>
            ),
          }}
        />
      </Stack>

      <Box height={47} />
      <Stack direction={"row"} spacing={4}>
        <SingleSelect
          label="Time Zone*"
          elemId={"timezoneForStore"}
          enableFullWidth={true}
          sortOptions={false}
          readOnlyFlag={false}
          options={
            typeof userStateDetails.infra_timezones !== "undefined" ? userStateDetails.infra_timezones : []
          }
          defaultVal={
            typeof userStateDetails.new_store_loc_data !== "undefined" &&
            typeof userStateDetails.new_store_loc_data.selected_timezone !== "undefined"
              ? {
                  label: userStateDetails.new_store_loc_data.selected_timezone,
                  id: userStateDetails.new_store_loc_data.selected_timezone_id,
                }
              : ""
          }
          selectionCheck={(option, value) => option?.id === value?.id}
          onChangeCall={(selectedOption) => {
            handleFieldChange({
              selected_timezone: selectedOption.label,
              selected_timezone_id: selectedOption.id,
            });
          }}
          fieldError={fieldErr("selected_timezone")}
        />
        {/* Branch: flat list. Cyreen and tenant: company tree, empty until they pick. */}
        <StoreOwnerField fieldError={fieldErr("share_list_selection")} />
      </Stack>

      {/* New region form — shown when user selects "Create new region" */}
      {newRegion && (
        <>
          <Box height={20} />
          <StyledDarkRedInformationTypography variant="body2">New Region Details</StyledDarkRedInformationTypography>
          <Box height={10} />
          <Stack direction={"row"} spacing={4}>
            <TextInputField
              fieldRequired={true}
              elemId="new_region_name"
              label="Region Name"
              fieldName="new_region_name"
              showlabel={true}
              defaultVal={userStateDetails?.new_store_loc_data?.new_region_name ?? ""}
              inputType={"text"}
              onChangeCall={(e) => {
                e.preventDefault();
                handleFieldChange({ new_region_name: e.target.value });
              }}
              fieldError={fieldErr("new_region_name")}
            />
            <TextInputField
              fieldRequired={true}
              elemId="new_region_code"
              label="Region Code"
              fieldName="new_region_code"
              showlabel={true}
              defaultVal={userStateDetails?.new_store_loc_data?.new_region_code ?? ""}
              inputType={"text"}
              onChangeCall={(e) => {
                e.preventDefault();
                handleFieldChange({ new_region_code: e.target.value });
              }}
              fieldError={fieldErr("new_region_code")}
            />
          </Stack>
        </>
      )}

      <Box height={47} />
    </>
  );
};

export default StoreGeneralInfo;
